import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// CORE DATA STRUCTURE  (as specified by user)
// ──────────────────────────────────────────────────────────
export type NotifCategory =
  | 'admission'
  | 'fee'
  | 'attendance'
  | 'exam'
  | 'staff'
  | 'transport'
  | 'announcement'
  | 'system'
  | 'leave'
  | 'timetable';

export interface Notification {
  id:        number;
  icon:      string;
  icon_bg:    string;
  ntext:      string;
  ntime:      string;
  unread:    boolean;
  category?: NotifCategory;
  ndetail?:   string;
  ntimestamp?:string;
}

type FilterType = 'all' | 'unread' | 'read' | NotifCategory;

// ──────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────
@Component({
  selector:    'app-notifications',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
	providers:   [DatePipe],
  templateUrl: './notifications.html',
  styleUrl:    './notifications.scss',
})
export class Notifications implements OnInit {
  dataService = inject(DataService);
	datePipe = inject(DatePipe);
  base = 'notifications';		// important for API calls
	loading = false;
	isEdit = false;
	today = new Date().toISOString().split('T')[0];

  activeFilter  = signal<FilterType>('all');
  searchQuery   = '';
  showDeleteAll = signal(false);
  toastMsg      = signal('');

  // ── Form state ────────────────────────────────────────
  showForm      = signal(false);
  isEditMode    = signal(false);
  editingId     = signal<number | null>(null);
  formSubmitted = false;

  form: Partial<Notification> = {};
  formErrors: Record<string, string> = {};

  // ── Icon & Color pickers (available options) ──────────
  readonly iconOptions = [
    // People & Admission
    { value: 'bi-person-plus',          label: 'Person Add'   },
    { value: 'bi-person-check',         label: 'Person Check' },
    { value: 'bi-person-x',             label: 'Person Remove'},
    { value: 'bi-people-fill',          label: 'People'       },
    { value: 'bi-person-badge',         label: 'Staff Badge'  },
    // Finance
    { value: 'bi-cash',                 label: 'Cash'         },
    { value: 'bi-cash-coin',            label: 'Fee Paid'     },
    { value: 'bi-credit-card-fill',     label: 'Card Payment' },
    { value: 'bi-wallet-fill',          label: 'Wallet'       },
    // Alerts & Status
    { value: 'bi-exclamation-triangle', label: 'Warning'      },
    { value: 'bi-exclamation-circle',   label: 'Alert'        },
    { value: 'bi-check-circle-fill',    label: 'Success'      },
    { value: 'bi-x-circle-fill',        label: 'Error'        },
    { value: 'bi-info-circle-fill',     label: 'Info'         },
    // Academic
    { value: 'bi-calendar-check',       label: 'Exam'         },
    { value: 'bi-calendar-x',           label: 'Absent'       },
    { value: 'bi-journal-check',        label: 'Report'       },
    { value: 'bi-mortarboard-fill',     label: 'Academic'     },
    { value: 'bi-book-fill',            label: 'Study'        },
    // Communication
    { value: 'bi-bell-fill',            label: 'Bell'         },
    { value: 'bi-megaphone',            label: 'Announcement' },
    { value: 'bi-chat-text-fill',       label: 'Message'      },
    { value: 'bi-envelope-fill',        label: 'Email'        },
    // Operations
    { value: 'bi-bus-front',            label: 'Transport'    },
    { value: 'bi-tools',                label: 'Maintenance'  },
    { value: 'bi-gear-wide-connected',  label: 'System'       },
    { value: 'bi-shield-lock',          label: 'Security'     },
    { value: 'bi-calendar-week',        label: 'Timetable'    },
    { value: 'bi-calendar-event',       label: 'Leave'        },
    { value: 'bi-trophy',               label: 'Achievement'  },
    { value: 'bi-star-fill',            label: 'Star'         },
  ];

  readonly colorOptions: { value: string; label: string; gradient: string }[] = [
    { value: 'bg-primary',   label: 'Blue',   gradient: 'linear-gradient(135deg,#1a3a6b,#2d6a9f)' },
    { value: 'bg-success',   label: 'Green',  gradient: 'linear-gradient(135deg,#059669,#10b981)' },
    { value: 'bg-warning',   label: 'Amber',  gradient: 'linear-gradient(135deg,#d97706,#f59e0b)' },
    { value: 'bg-danger',    label: 'Red',    gradient: 'linear-gradient(135deg,#dc2626,#ef4444)' },
    { value: 'bg-info',      label: 'Cyan',   gradient: 'linear-gradient(135deg,#0284c7,#38bdf8)' },
    { value: 'bg-secondary', label: 'Gray',   gradient: 'linear-gradient(135deg,#374151,#6b7280)' },
    { value: 'bg-dark',      label: 'Dark',   gradient: 'linear-gradient(135deg,#111827,#374151)' },
    { value: 'bg-purple',    label: 'Purple', gradient: 'linear-gradient(135deg,#7c3aed,#a78bfa)' },
    { value: 'bg-teal',      label: 'Teal',   gradient: 'linear-gradient(135deg,#0d9488,#2dd4bf)' },
    { value: 'bg-pink',      label: 'Pink',   gradient: 'linear-gradient(135deg,#be185d,#ec4899)'  },
  ];

  // ── The core notifications array ──────────────────────
  private nextId = 16;
  notifications = signal<Notification[]>([
    // { id: 1,  icon:'bi-person-plus',              icon_bg:'bg-primary',    text:'New admission application received from Arjun Sharma (Class XI – Science)',            time:'5 min ago',    unread:true,  category:'admission',   timestamp:'2026-08-03T04:50:00Z', detail:'Application ID: APP-2526-0042 · Category: General · Board: CBSE' },
    // { id: 2,  icon:'bi-cash',                     icon_bg:'bg-warning',    text:'8 fee payments are pending approval for this month',                                    time:'1 hour ago',   unread:true,  category:'fee',         timestamp:'2026-08-03T03:55:00Z', detail:'Total pending: ₹1,84,500 · Classes: IX, X, XI, XII' },
    // { id: 3,  icon:'bi-exclamation-triangle',     icon_bg:'bg-danger',     text:'12 students have attendance below 75% — parental notification required',              time:'3 hours ago',  unread:true,  category:'attendance',  timestamp:'2026-08-03T01:55:00Z', detail:'Class XI-A: 4 students · Class X-B: 5 students · Class IX-C: 3 students' },
    // { id: 4,  icon:'bi-calendar-check',           icon_bg:'bg-success',    text:'Mid-term exam schedule published for Class IX and X',                                  time:'5 hours ago',  unread:false, category:'exam',        timestamp:'2026-08-02T23:55:00Z', detail:'Exam period: Aug 20–28, 2026 · Download timetable from Examinations module' },
    // { id: 5,  icon:'bi-megaphone',                icon_bg:'bg-info',       text:'Annual Day celebration announced — 15th August 2026',                                  time:'1 day ago',    unread:false, category:'announcement',timestamp:'2026-08-02T04:55:00Z', detail:'Venue: School Auditorium · Time: 5:00 PM · All staff and students to attend' },
    // { id: 6,  icon:'bi-person-x',                 icon_bg:'bg-secondary',  text:'Leave application from Deepak Yadav (Transport Driver) pending review',               time:'1 day ago',    unread:true,  category:'leave',       timestamp:'2026-08-02T03:50:00Z', detail:'From: Aug 5 to Aug 8 · Reason: Medical · 4 days' },
    // { id: 7,  icon:'bi-shield-lock',              icon_bg:'bg-dark',       text:'System maintenance scheduled — Sunday 9 Aug 2026 (11 PM – 2 AM)',                    time:'2 days ago',   unread:false, category:'system',      timestamp:'2026-08-01T04:55:00Z', detail:'Services affected: Fee Portal, Student Portal, Reports · Plan accordingly' },
    // { id: 8,  icon:'bi-bus-front',                icon_bg:'bg-warning',    text:'Bus RT-02 insurance expires in 28 days — renewal required',                          time:'2 days ago',   unread:true,  category:'transport',   timestamp:'2026-08-01T03:55:00Z', detail:'Vehicle: DL 02 CD 5678 · Insurance Expiry: 30 Sep 2026' },
    // { id: 9,  icon:'bi-journal-check',            icon_bg:'bg-success',    text:'Class XI Science timetable has been updated for the new term',                        time:'3 days ago',   unread:false, category:'timetable',   timestamp:'2026-07-31T04:55:00Z', detail:'Updated by: Admin User · Effective from: 4 Aug 2026' },
    // { id: 10, icon:'bi-person-check',             icon_bg:'bg-primary',    text:'Staff record for Mrs. Priya Sharma updated — designation change to Senior Teacher',   time:'3 days ago',   unread:false, category:'staff',       timestamp:'2026-07-31T03:50:00Z', detail:'Previous: Teacher → New: Senior Teacher · HR approved' },
    // { id: 11, icon:'bi-cash-coin',                icon_bg:'bg-success',    text:'Fee collection for July 2026 completed — ₹18.4 Lakhs collected',                     time:'4 days ago',   unread:false, category:'fee',         timestamp:'2026-07-30T04:55:00Z', detail:'Collection rate: 91.4% · Pending: ₹1.6L from 28 students' },
    // { id: 12, icon:'bi-person-plus',              icon_bg:'bg-primary',    text:'3 new admission applications received — Class IX (2), BCA (1)',                      time:'4 days ago',   unread:false, category:'admission',   timestamp:'2026-07-30T03:55:00Z', detail:'Review pending in Eligibility module' },
    // { id: 13, icon:'bi-calendar-x',               icon_bg:'bg-danger',     text:'3 students absent for 5 consecutive days — parent contact required',                 time:'5 days ago',   unread:true,  category:'attendance',  timestamp:'2026-07-29T04:55:00Z', detail:'Rahul Mishra (XI-A), Ravi Gupta (XI-A), Deepak Singh (IX-B)' },
    // { id: 14, icon:'bi-trophy',                   icon_bg:'bg-warning',    text:'Sports Day results announced — Red House wins the championship',                      time:'5 days ago',   unread:false, category:'announcement',timestamp:'2026-07-29T03:50:00Z', detail:'Overall: Red House 420 pts · Blue House 385 pts · Green House 362 pts' },
    // { id: 15, icon:'bi-gear-wide-connected',      icon_bg:'bg-dark',       text:'New module activated: Subscription upgraded to Standard Plan',                        time:'6 days ago',   unread:false, category:'system',      timestamp:'2026-07-28T04:55:00Z', detail:'Active add-ons: Fee Management, Transport, SMS Notifications' },
  ]);

  // ── Computed ──────────────────────────────────────────
  readonly unreadCount = computed(() => this.notifications()?.filter((n: any) => n.unread).length ?? 0);

  readonly filtered = computed(() => {
    const q = this.searchQuery.toLowerCase().trim();
    const f = this.activeFilter();
    return this.notifications()?.filter(n => {
      const matchQ = !q || n.ntext.toLowerCase().includes(q) || (n.ndetail ?? '').toLowerCase().includes(q);
      const matchF =
        f === 'all'    ? true :
        f === 'unread' ? n.unread :
        f === 'read'   ? !n.unread :
        n.category === f;
      return matchQ && matchF;
    });
  });

  readonly grouped = computed(() => {
    const groups = new Map<string, Notification[]>();
    this.filtered()?.forEach(n => {
      const key = this.getGroupKey(n.ntimestamp ?? '');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(n);
    });
    return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
  });

  readonly categoryCounts = computed(() => {
    const map = new Map<string, number>();
    this.notifications()?.forEach(n => {
      if (n.category) map.set(n.category, (map.get(n.category) ?? 0) + 1);
    });
    return map;
  });

  readonly unreadByCat = computed(() => {
    const map = new Map<string, number>();
    this.notifications()?.filter(n => n.unread).forEach(n => {
      if (n.category) map.set(n.category, (map.get(n.category) ?? 0) + 1);
    });
    return map;
  });

  // Live preview (uses form values)
  readonly previewNotif = computed((): Notification => ({
    id:         0,
    icon:       this.form.icon     ?? 'bi-bell-fill',
    icon_bg:    this.form.icon_bg   ?? 'bg-primary',
    ntext:      this.form.ntext     || 'Your notification message will appear here…',
    ntime:      this.form.ntime     || 'Just now',
    unread:     this.form.unread   ?? true,
    category:   this.form.category,
    ndetail:    this.form.ndetail,
    ntimestamp: new Date().toISOString(),
  }));

  readonly selectedColorGradient = computed(() =>
    this.colorOptions.find(c => c.value === (this.form.icon_bg ?? 'bg-primary'))?.gradient
    ?? 'linear-gradient(135deg,#1a3a6b,#2d6a9f)'
  );

  // ── Category config ───────────────────────────────────
  readonly categories: { key: NotifCategory; label: string; icon: string; color: string }[] = [
    { key:'admission',    label:'Admission',    icon:'bi-person-plus',        color:'#1a3a6b' },
    { key:'fee',          label:'Fee',          icon:'bi-cash-coin',          color:'#d97706' },
    { key:'attendance',   label:'Attendance',   icon:'bi-calendar-check',     color:'#dc2626' },
    { key:'exam',         label:'Exam',         icon:'bi-journal-check',      color:'#059669' },
    { key:'staff',        label:'Staff',        icon:'bi-person-badge',       color:'#7c3aed' },
    { key:'transport',    label:'Transport',    icon:'bi-bus-front',          color:'#ea580c' },
    { key:'announcement', label:'General',      icon:'bi-megaphone',          color:'#0891b2' },
    { key:'system',       label:'System',       icon:'bi-gear',               color:'#374151' },
    { key:'leave',        label:'Leave',        icon:'bi-calendar-event',     color:'#be185d' },
    { key:'timetable',    label:'Timetable',    icon:'bi-calendar-week',      color:'#4338ca' },
  ];

  async ngOnInit() {
		this.loading = true;
    this.dataService.getAllRecords(this.base).then((res: any) => {
      this.notifications.set(res);
      this.loading = false;
    });

  }

  // ── FORM METHODS ─────────────────────────────────────
  openAddForm(): void {
    this.form = {
      icon:      'bi-bell-fill',
      icon_bg:    'bg-primary',
      ntext:      '',
      ndetail:    '',
      ntime:      'Just now',
      unread:    true,
      category:  undefined,
    };
    this.formErrors    = {};
    this.formSubmitted = false;
    this.isEditMode.set(false);
    this.editingId.set(null);
    this.showForm.set(true);
    scrollTo(0, 0);
  }

  openEditForm(n: Notification): void {
    this.form = {
      icon:      n.icon,
      icon_bg:   n.icon_bg,
      ntext:     n.ntext,
      ndetail:   n.ndetail ?? '',
      ntime:     n.ntime,
      unread:    n.unread,
      category:  n.category,
    };
    this.formErrors    = {};
    this.formSubmitted = false;
    this.isEditMode.set(true);
    this.editingId.set(n.id);
    this.showForm.set(true);
    scrollTo(0, 0);
  }

  closeForm(): void {
    this.showForm.set(false);
    this.formSubmitted = false;
    this.formErrors    = {};
  }

  saveForm(): void {
    this.formSubmitted = true;
    this.formErrors    = {};

    if (!this.form.ntext?.trim())  this.formErrors['text'] = 'Notification message is required.';
    if (!this.form.icon)           this.formErrors['icon'] = 'Please select an icon.';
    if (!this.form.icon_bg)        this.formErrors['icon_bg'] = 'Please select a colour.';
    if (!this.form.ntime?.trim())  this.form.ntime = 'Just now';

    if (Object.keys(this.formErrors).length) return;

    const now = new Date().toISOString();

    if (this.isEditMode() && this.editingId() !== null) {
      this.notifications.update(list =>
        list.map(n => n.id === this.editingId()
          ? {
              ...n,
              icon:      this.form.icon!,
              icon_bg:   this.form.icon_bg!,
              ntext:     this.form.ntext!.trim(),
              ndetail:   this.form.ndetail?.trim() ?? '',
              ntime:     this.form.ntime!.trim(),
              unread:    this.form.unread ?? n.unread,
              category:  this.form.category,
            }
          : n
        )
      );

      this.doUpdate();
      this.flash('Notification updated successfully!');
    } else {
      if(this.notifications()?.length) {
        this.notifications.update((list: any) => [{
          id:        this.nextId++,
          icon:      this.form.icon!,
          icon_bg:   this.form.icon_bg!,
          ntext:     this.form.ntext!.trim(),
          ndetail:   this.form.ndetail?.trim() ?? '',
          ntime:     this.form.ntime!.trim(),
          unread:    this.form.unread ?? true,
          category:  this.form.category,
          ntimestamp: now,
        }, ...list]);
      } else {
        this.notifications.set([{
          id:        this.nextId++,
          icon:      this.form.icon!,
          icon_bg:   this.form.icon_bg!,
          ntext:     this.form.ntext!.trim(),
          ndetail:   this.form.ndetail?.trim() ?? '',
          ntime:     this.form.ntime!.trim(),
          unread:    this.form.unread ?? true,
          category:  this.form.category,
          ntimestamp: now,
        }]);
      }

      this.doSave();
      this.flash('Notification added successfully!');
    }

    this.closeForm();
  }

  // ── EXISTING ACTIONS ─────────────────────────────────
  markRead(id: number): void {
    this.notifications.update(list => list.map(n => n.id === id ? { ...n, unread: false } : n));
  }

  markAllRead(): void {
    this.notifications.update(list => list.map(n => ({ ...n, unread: false })));
    this.flash('All notifications marked as read.');
  }

  deleteNotification(id: number): void {
    this.notifications.update(list => list.filter(n => n.id !== id));
  }

  deleteAll(): void {
    const f = this.activeFilter();
    if (f === 'all')         this.notifications.set([]);
    else if (f === 'unread') this.notifications.update(l => l.filter(n => !n.unread));
    else if (f === 'read')   this.notifications.update(l => l.filter(n => n.unread));
    else                     this.notifications.update(l => l.filter(n => n.category !== f));
    this.showDeleteAll.set(false);
    this.flash('Notifications cleared.');
  }

  toggleUnread(id: number): void {
    this.notifications.update(list =>
      list.map(n => n.id === id ? { ...n, unread: !n.unread } : n)
    );
  }

  // ── HELPERS ───────────────────────────────────────────
  private getGroupKey(timestamp: string): string {
    if (!timestamp) return 'Earlier';
    const d   = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yest  = new Date(today.getTime() - 86400000);
    const nd    = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (nd.getTime() === today.getTime()) return 'Today';
    if (nd.getTime() === yest.getTime())  return 'Yesterday';
    const diff = Math.floor((today.getTime() - nd.getTime()) / 86400000);
    if (diff <= 7) return 'This Week';
    return 'Earlier';
  }

  getFilterLabel(): string {
    const f = this.activeFilter();
    if (f === 'all')    return 'All Notifications';
    if (f === 'unread') return 'Unread';
    if (f === 'read')   return 'Read';
    return this.categories.find(c => c.key === f)?.label ?? f;
  }

  getCategoryLabel(cat: NotifCategory): string { return this.categories.find(c => c.key === cat)?.label ?? cat; }
  getCategoryIcon(cat: NotifCategory): string  { return this.categories.find(c => c.key === cat)?.icon ?? 'bi-bell'; }
  getCategoryColor(cat: NotifCategory): string { return this.categories.find(c => c.key === cat)?.color ?? '#374151'; }

  getIconLabel(icon: string): string { return this.iconOptions.find(i => i.value === icon)?.label ?? icon; }
  getColorGradient(bg: string): string { return this.colorOptions.find(c => c.value === bg)?.gradient ?? ''; }
  getColorLabel(bg: string): string { return this.colorOptions.find(c => c.value === bg)?.label ?? bg; }

  private flash(msg: string): void {
    this.toastMsg.set(msg);
    setTimeout(() => this.toastMsg.set(''), 2500);
  }


  // ── ACTIONS ───────────────────────────────────────────
  doSave() {
    this.loading = true;
    this.dataService.addNewRecord(this.base, this.form).subscribe({
      next: (response) => {
        // console.log('Response received in component:', response);
        this.loading = false;
				this.isEdit = false;
        // this.resetForm();
				this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate() {
    this.loading = true;
		const myid: any = this.editingId();
		delete this.form.id;

    this.dataService.updateRecord(this.base, myid, this.form).subscribe({
      next: (response) => {
        // console.log('Response received in component:', response);
        this.loading = false;
				this.isEdit = false;
        // this.resetForm();
				this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

	async deleteClick(myid: number) {
    const confirmed = await this.dataService.confirmDelete();
    if (confirmed) {
        console.log('Item deleted successfully');
				this.doDelete(myid);
    } else {
        console.log('Deletion cancelled');
    }
	}

  doDelete(myid: number) {
		this.loading = true;
    this.dataService.deleteRecord(this.base, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
